/**
 * English FAQ translations
 * Source of truth - all other languages should match this structure
 * Last Updated: January 5, 2026 | Version 3.0
 */
export const faq = {
  // Page Meta
  pageTitle: "Frequently Asked Questions",
  heroSubtitle: "Everything You Need to Know About the Youth Futures Observatory",
  heroDescription: "Comprehensive answers about how the Observatory works, how the Youth Futures Index was developed, and how you can use or contribute to this platform.",
  lastUpdated: "Last Updated: January 5, 2026",
  version: "Version 3.0",
  availableLanguages: "Available in English, Portuguese, Spanish, French, and Japanese",

  // Section Navigation
  sectionUnderstanding: "Understanding",
  sectionMethodology: "Methodology",
  sectionData: "Data & Sources",
  sectionUsing: "Using the Observatory",
  sectionFuture: "Future Development",
  sectionEvolve: "2026 Plan",
  sectionCommon: "Common Questions",
  sectionContact: "Contact",

  // Section 1: Understanding the Observatory
  understandingTitle: "Understanding the Observatory",
  
  // Q1: What is the Youth Futures Observatory?
  q1Title: "What is the Youth Futures Observatory?",
  q1Answer: "The Youth Futures Observatory is an evidence-based intelligence platform that combines verified data from JCI's institutional records and global youth research to map youth opportunity worldwide. It's designed to help JCI National and Local Organizations understand the broader context in which they operate and identify where their leadership development efforts have the greatest impact.",
  q1Detail: "Rather than simply counting JCI members or programs, the Observatory asks a fundamental question: How does JCI contribute to expanding opportunity for young people worldwide?",
  q1Integrates: "It answers this by integrating:",
  q1IntegratesJCI: "JCI Institutional Data — Actual members, programs, participation from members across 114 countries",
  q1IntegratesGlobal: "Global Youth Opportunity Indices — Comparative data on mental health, workforce development, and entrepreneurship across the same 114 countries",
  q1IntegratesStrategic: "Strategic Documentation — Vision 2040 targets and JCI's growth ambitions through 2040",
  q1Result: "The result is a strategic tool that shows JCI where to expand, what to prioritize, and how to measure success in advancing global youth opportunity.",

  // Q2: Why "Youth Futures"?
  q2Title: "Why is it called 'Youth Futures' Observatory?",
  q2Answer: "'Youth Futures' reflects an important concept from futures studies — the academic field that examines how decisions and actions shape possible futures. The name encodes three key ideas:",
  q2Idea1Title: "The Plural of 'Futures'",
  q2Idea1: "Young people don't have a single predetermined future. They have multiple possible futures depending on available opportunities, access to resources, quality of education, mental health support, and entrepreneurial ecosystem strength.",
  q2Idea2Title: "The Futures Studies Perspective",
  q2Idea2: "Futures studies teaches us that the future is not predetermined — it's shaped by choices we make today. By analyzing current conditions, we're mapping the likely futures young people will inherit. By identifying gaps, we're identifying where JCI can intervene to create better futures.",
  q2Idea3Title: "Data-Driven Decision Making",
  q2Idea3: "Understanding these futures requires rigorous data about what's happening right now in each region, so leaders can make informed decisions about where their efforts will have the greatest impact.",
  q2Summary: "The Observatory gives voice to the reality that youth opportunity is not evenly distributed globally. A young person in Europe faces vastly different opportunity landscapes than a young person in Africa and the Middle East.",

  // Q3: Why was it created?
  q3Title: "Why did the JCI Development Councils create this Observatory?",
  q3Answer: "The 4th Strategic Goal of JCI's 2023-2027 Strategic Plan is Data-Driven Innovation. This goal states: 'To be a leading organization, we need to stay on the cutting edge of technology and innovation.'",
  q3Questions: "The Observatory was created to answer three questions:",
  q3Question1: "How do we prove our impact? — JCI needed a rigorous, auditable way to demonstrate impact through data, not just stories.",
  q3Question2: "Where should we focus our efforts? — With 4,641 Local Organizations across 114 countries, JCI needs strategic intelligence about where efforts have the greatest impact.",
  q3Question3: "How do we align with global priorities? — By mapping JCI's work against global youth opportunity indices, JCI can demonstrate alignment with UN SDGs and position itself as a strategic partner.",
  q3Conclusion: "The Observatory is Data-Driven Innovation in practice — using verified data and systematic analysis to guide strategic decisions.",

  // Characteristics (preserved from current design)
  characteristics: {
    dataBased: "Based on Real Data",
    dataBasedDesc: "Only verifiable and auditable data",
    transparent: "Complete Transparency",
    transparentDesc: "Methodology fully exposed",
    collaborative: "Collaborative",
    collaborativeDesc: "Built with contributions from the entire JCI community",
    accessible: "Accessible",
    accessibleDesc: "Free and open for non-commercial use",
    scientific: "Scientific",
    scientificDesc: "Aligned with international research standards",
    inclusive: "Inclusive",
    inclusiveDesc: "Reflects the global diversity of JCI",
  },

  // Section 2: Observatory Methodology
  methodologyTitle: "Observatory Methodology",

  // Q4: How was the YFI developed?
  q4Title: "How was the Youth Futures Index developed?",
  q4Answer: "The Youth Futures Index (YFI) is not a new invention created for JCI. Rather, it's a synthesis and adaptation of established global research methodologies that measure youth opportunity.",
  q4Steps: "Here's how it was developed:",
  q4Step1Title: "Step 1: Identified Existing Frameworks",
  q4Step1: "Research teams reviewed existing youth opportunity indices from global institutions: WHO Mental Health Atlas, ILO employment databases, UNESCO education statistics, Global Entrepreneurship Monitor studies.",
  q4Step2Title: "Step 2: Designed YFI Structure",
  q4Step2: "These dimensions were integrated into a single composite index using research-backed weighting:",
  q4Weight1: "Mental Health: 40% (service access, prevalence rates, suicide mortality)",
  q4Weight2: "Workforce & Education: 30% (youth unemployment, NEET rates, skills development)",
  q4Weight3: "Entrepreneurship: 30% (business environment, access to financing, innovation capacity)",
  q4WeightRationale: "The weighting reflects research showing that youth opportunity depends most heavily on mental health (which enables all other capabilities), followed by workforce readiness, then entrepreneurial capacity.",
  q4Step3Title: "Step 3: Applied to 114 JCI Countries",
  q4Step3: "The YFI framework was calculated for all 114 countries where JCI operates, creating a global map of youth opportunity that directly aligns with JCI's geographic footprint.",
  q4Step4Title: "Step 4: Validated Results",
  q4Step4: "Results were cross-checked against JCI member feedback from 2025 surveys and published global research on youth development.",
  q4Result: "The Result: A rigorous, auditable index grounded in established research, not speculation or models.",

  // YFI Formula
  yfiFormulaTitle: "YFI Formula",
  yfiFormula: "YFI = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
  yfiMH: "MH = Mental Health component (40% weight)",
  yfiWF: "WF = Workforce & Education component (30% weight)",
  yfiENT: "ENT = Entrepreneurship component (30% weight)",

  // Q5: Dashcity Methodology
  q5Title: "How does the Dashcity 5-Phase Methodology work?",
  q5Answer: "The Dashcity 5-Phase Methodology provides the strategic structure for how the Observatory was built and how it will evolve. Each phase has a specific objective:",
  
  phase1: "IDENTIFY",
  phase1Status: "Completed",
  phase1Dates: "Sep-Nov 2025",
  phase1Objective: "Map the complete youth leadership ecosystem",
  phase1Activities: "Analyzed JCI Strategic Plan 2023-2027; Diagnosed institutional landscape (114 countries, 4,641 local organizations, 148,122 members); Identified the gap: JCI data existed in silos without unified analysis",
  phase1Deliverable: "Strategic framework & research scope",

  phase2: "MEASURE",
  phase2Status: "Completed",
  phase2Dates: "Oct-Dec 2025",
  phase2Objective: "Establish rigorous data standards and validation processes",
  phase2Activities: "Established 7 non-negotiable quality criteria; Verified all primary data sources (JCI Impact Report, surveys, Vision 2040); Integrated global data sources (WHO, ILO, UNESCO, World Bank, GEM); Implemented 5-step validation process",
  phase2Deliverable: "Data architecture & quality standards",

  phase3: "PROMOTE",
  phase3Status: "In Progress",
  phase3Dates: "Dec 2025-Jan 2026",
  phase3Objective: "Communicate value and engage stakeholders",
  phase3Activities: "Developed stakeholder engagement channels; Built contribution guidelines and incentive structures; Produced multilingual resources (EN, PT, ES, FR, JA)",
  phase3Deliverable: "Communication toolkit & engagement infrastructure",

  phase4: "DIRECT",
  phase4Status: "In Progress",
  phase4Dates: "Jan-Feb 2026",
  phase4Objective: "Establish governance and quality oversight",
  phase4Activities: "Documented validation processes (7-step quality gate); Defined update cycles (annual, intermediate, continuous); Established feedback loops (Community → Teams → Councils → Board)",
  phase4Deliverable: "Governance framework & operational processes",

  phase5: "SCALE",
  phase5Status: "Planned",
  phase5Dates: "Jan 2026 - Dec 2027+",
  phase5Objective: "Expand reach, impact, and global influence",
  phase5Activities: "Creating communication materials for different audiences; Scale to 15+ National Organizations actively contributing; Grow to 1,000+ registered users; Launch public APIs for academic researchers",
  phase5Deliverable: "Global authority on youth leadership development",

  phaseWhyTitle: "Why This Approach Works",
  phaseWhy: "Each phase builds on the previous one. IDENTIFY establishes what we're building. MEASURE ensures rigor. PROMOTE engages stakeholders. DIRECT ensures governance. SCALE ensures sustainable growth.",

  // Q6: Areas of Opportunity
  q6Title: "What are the 4 JCI Areas of Opportunity?",
  areaIndividual: "Individual Development",
  areaIndividualDesc: "Young people develop essential leadership skills, confidence, and personal growth through training, mentoring, and hands-on experience.",
  areaIndividualExamples: "Examples: Public Speaking, debating, leadership courses, skills certification",
  
  areaCommunity: "Community Impact",
  areaCommunityDesc: "Young leaders identify community needs and design solutions that benefit society.",
  areaCommunityExamples: "Examples: Health initiatives, education programs, disaster relief, environmental projects",
  
  areaInternational: "International Cooperation",
  areaInternationalDesc: "Young leaders build global networks, understand diverse perspectives, and collaborate across borders.",
  areaInternationalExamples: "Examples: JCI Twinning partnerships, TOYP award, international events, human rights advocacy",
  
  areaBusiness: "Business & Entrepreneurship",
  areaBusinessDesc: "Young leaders launch businesses, drive innovation, and build economic opportunity.",
  areaBusinessExamples: "Examples: Creative Young Entrepreneur, JCI in Business, startup accelerators",

  // Q7: Connection between Areas and Observatory
  q7Title: "What's the connection between the 4 Areas and the Observatory?",
  q7Answer: "The Observatory measures the external context into which these 4 Areas operate.",
  q7Detail: "The YFI tracks: Mental Health (affects all 4 Areas — healthy minds learn better, build better), Workforce & Education (supports Individual Development + Business areas), Entrepreneurship (supports Business area).",
  q7Example: "When the Observatory shows that Africa and the Middle East has a YFI score of 53 (vs. Europe at 75), it's saying: 'Youth in these regions face mental health, employment, and business opportunity gaps. This is where JCI's 4 Areas of Opportunity can have maximum impact.'",
  q7Summary: "The 4 Areas are how JCI develops leaders. The Observatory shows where and why that matters most.",

  // Q8: Data-Driven Innovation
  q8Title: "What is Data-Driven Innovation (the 4th Strategic Goal)?",
  q8Answer: "The 4th Strategic Goal addresses critical questions: How do we know if our leadership development is actually working? How do we improve? Where should we focus?",
  q8Means: "Data-Driven Innovation means:",
  q8Means1: "Measuring what matters — Using rigorous data about youth opportunity, JCI membership, and impact",
  q8Means2: "Analyzing for insights — Finding patterns (e.g., 'mental health access varies 22 points between regions')",
  q8Means3: "Making informed decisions — Allocating resources based on evidence",
  q8Means4: "Continuous improvement — Gathering feedback, adjusting strategy, measuring results",
  q8Conclusion: "The Observatory transforms JCI from an organization that feels it makes a difference to one that can prove and measure its impact using verified, auditable data.",

  // Section 3: Data and Sources
  dataSourcesTitle: "Data & Sources",

  // Q9: What data sources?
  q9Title: "What data sources does the Observatory use?",
  primaryDataTitle: "Primary JCI Data",
  primaryData1: "JCI Impact Report 2025 — 147,670 members, 4,641 local organizations, 1,000+ annual projects, 21M+ beneficiaries",
  primaryData2: "JCI Vision 2040 — Strategic roadmap, growth targets, technology innovation plan",
  primaryData3: "2025 Membership Experience Survey — 3,767 respondents across all 4 regions",
  
  secondaryDataTitle: "Secondary Global Data",
  secondaryData1: "WHO Mental Health Atlas 2024 — Mental health service coverage, prevalence estimates",
  secondaryData2: "WHO Mortality Database — Suicide mortality rates by country",
  secondaryData3: "Sapien Labs Mental State of the World 2024 — Subjective wellbeing research",
  secondaryData4: "ILO Global Employment Trends 2024-25 — Youth unemployment, NEET statistics",
  secondaryData5: "UNESCO Institute for Statistics — Education enrollment, literacy rates",
  secondaryData6: "World Bank — Human Capital Index, Ease of Doing Business",
  secondaryData7: "GEM Global Entrepreneurship Monitor 2024-25 — Entrepreneurial activity",
  secondaryData8: "ITU Digital Development Dashboard — Digital infrastructure and access",
  
  dataTransparency: "All sources are public and verified. The Observatory contains zero proprietary, confidential, or restricted data.",

  // Q10: Data quality
  q10Title: "How is data quality ensured?",
  q10Answer: "We implemented 7 non-negotiable quality criteria:",
  qualityCriteria1: "Verifiable — Clear source identification (e.g., 'WHO Mental Health Atlas 2024, page 12')",
  qualityCriteria2: "Auditable — Complete methodology documented",
  qualityCriteria3: "Traceable — Identified origin, no anonymity",
  qualityCriteria4: "Accurate — Validated before presentation",
  qualityCriteria5: "Current — Clear date and temporal relevance",
  qualityCriteria6: "Contextualized — Limitations clearly explained",
  qualityCriteria7: "Cited — Complete credit to sources",

  validationProcessTitle: "5-Step Validation Process",
  validationStep1: "Submission (1-2 days)",
  validationStep1Desc: "Data intake and preliminary completeness check",
  validationStep2: "Validation (5-7 days)",
  validationStep2Desc: "Technical verification of methodology and calculations",
  validationStep3: "Audit (5-7 days)",
  validationStep3Desc: "Independent audit of data accuracy",
  validationStep4: "Peer Review (5-7 days)",
  validationStep4Desc: "Subject matter expert review",
  validationStep5: "Publication (2-3 days)",
  validationStep5Desc: "Final certification and transparency documentation",

  // Q11: Zero synthetic data
  q11Title: "What does 'zero synthetic data' mean?",
  q11Answer: "Zero synthetic data means: Every number you see traces to an actual data source. No numbers are invented, estimated, or generated by models.",
  
  notIncludedTitle: "We do NOT include",
  notIncluded1: "Projections presented as current facts",
  notIncluded2: "AI-generated statistics",
  notIncluded3: "Interpolated values filling data gaps",
  notIncluded4: "Model-based estimates (unless explicitly labeled)",
  notIncluded5: "Assumed distributions",
  
  includedTitle: "We DO include",
  included1: "Actual counts (e.g., '147,670 members')",
  included2: "Survey-based statistics (e.g., '3,767 respondents')",
  included3: "Published indices (e.g., 'YFI score: 75.28')",
  included4: "Explicitly-marked scenarios (e.g., '2040 Vision: 500,000 members' — clearly labeled as strategic target)",
  
  zeroSyntheticWhy: "Why this matters: You can trust these numbers. They're not generated by algorithms or estimated by consultants. They're verified facts from auditable sources.",

  // Section 4: Using the Observatory
  usingTitle: "Using the Observatory",

  // Q12: Update cycles
  q12Title: "How often is the data updated?",
  annualUpdatesTitle: "Annual Updates (Comprehensive)",
  annualUpdates1: "November-December: Data collection from all sources",
  annualUpdates2: "January-February: Validation and comparative analysis",
  annualUpdates3: "March: New edition publication",
  annualUpdates4: "April-May: Stakeholder feedback and engagement",
  
  intermediateUpdatesTitle: "Intermediate Updates (As Needed)",
  intermediateUpdates1: "New significant research findings",
  intermediateUpdates2: "Major organizational changes or program innovations",
  intermediateUpdates3: "Important policy developments",
  intermediateUpdates4: "Error corrections or clarifications",
  
  continuousUpdatesTitle: "Continuous Updates (Ongoing)",
  continuousUpdates1: "Link updates and reference corrections",
  continuousUpdates2: "Platform technical improvements",
  continuousUpdates3: "User interface enhancements",
  continuousUpdates4: "Accessibility improvements",
  
  updateGoal: "The goal is to keep the Observatory current and accurate without overwhelming users with constant micro-updates.",

  // Q13: Using the data
  q13Title: "Can I use this data for my research or organization?",
  q13Answer: "Yes, absolutely. The Observatory is designed for:",
  
  useNationalTitle: "National JCI Organizations",
  useNational1: "Benchmark your progress against global data",
  useNational2: "Identify where your regional leadership efforts have greatest impact",
  useNational3: "Make data-backed strategic decisions about where to expand",
  
  useLocalTitle: "Local JCI Organizations",
  useLocal1: "Understand the youth opportunity landscape in your city/region",
  useLocal2: "Connect your local projects to global opportunity challenges",
  useLocal3: "Track whether your work is addressing priority gaps",
  
  useAcademicTitle: "Academic Researchers",
  useAcademic1: "Access to verified data and published methodology",
  useAcademic2: "APIs for programmatic access (planned for 2026)",
  useAcademic3: "Permission to cite and build upon Observatory findings",
  
  usePolicyTitle: "Policymakers & Government",
  usePolicy1: "Evidence on youth opportunity in your country",
  usePolicy2: "Comparison with neighboring regions",
  usePolicy3: "Strategic intelligence for education, employment, mental health policy",
  
  useCorporateTitle: "Corporate Partners",
  useCorporate1: "Understand youth development needs in your markets",
  useCorporate2: "Identify where partnerships with JCI could be most impactful",
  useCorporate3: "Access verified data for corporate responsibility reporting",
  
  publicAPIs: "Public APIs — Launching Q2 2026 for researchers and developers",

  // Q14: Reproducibility
  q14Title: "Can researchers reproduce the methodology?",
  q14Answer: "Yes, completely. All components are documented and publicly available:",
  q14Formula: "YFI Formula (Publicly Published): YFI = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
  q14Sources: "Source Data (All Public): WHO Mental Health Atlas, ILO ILOSTAT Database, UNESCO statistics, World Bank indicators, GEM Global Entrepreneurship Monitor, JCI Impact Report 2025",
  q14Methodology: "Complete Methodology Documentation — Published in full at the Methodology page",
  q14Commitment: "Any researcher with access to these same sources can replicate the analysis, verify our calculations, and test our conclusions. That's the commitment to transparency.",

  // Section 5: Future Development
  futureTitle: "Future Development",

  // Q15: Future roadmap
  q15Title: "What is the future of the Observatory?",
  q15Answer: "The Observatory is evolving through a phased expansion plan:",
  
  roadmap2026Title: "2026 (BETA — Expansion)",
  roadmap2026Items: "Scale to 15+ National Organizations as active contributors; Grow user community to 200+ members; Launch public APIs for academic researchers; Develop advanced analysis dashboard; Begin documentation of policy influence",
  roadmap2026Target: "Targets: 200+ users, 10+ annual media articles citing Observatory",
  
  roadmap2027Title: "2027+ (SCALE — Global Authority)",
  roadmap2027Items: "Position as global reference for youth leadership development; Integration with UN (ECOSOC), WEF, UNESCO platforms; Visible policy influence in 10+ countries; Global community of 1,000+ engaged users; Annual publication recognized in top media outlets",
  roadmap2027Target: "Targets: 500+ annual media articles, influence on policies in 10+ countries",
  
  innovationsTitle: "Strategic Innovations Planned",
  innovation2026: "2026: Mentor Directory, Partnership Pages, Real-Time Advocacy Counter",
  innovation2027: "2027+: Monthly Research Newsletter, Expert Webinar Series, Interactive Skills Assessment Tools, Grant Matching Calculator",

  // Q16: Contributing
  q16Title: "How can I contribute to the Observatory?",
  
  contributeNationalTitle: "National Organizations can contribute",
  contributeNational: "Regional analysis and insights; Case studies of successful programs; Data from national events and programs; Feedback on how findings apply in your context",
  
  contributeLocalTitle: "Local Organizations can contribute",
  contributeLocal: "Project outcomes and impact metrics; Member development stories; Data on community reach and beneficiaries; Lessons learned from local initiatives",
  
  contributeMemberTitle: "Individual Members can contribute",
  contributeMember: "Feedback on skills learned through JCI programs; Stories of how JCI changed your leadership trajectory; Data on employment outcomes; Suggestions for improvements",
  
  contributeHowTitle: "How to contribute",
  contributeHow1: "Visit the Contributing page",
  contributeHow2: "Review contribution guidelines",
  contributeHow3: "Complete contribution form with required documentation",
  contributeHow4: "Receive feedback within 5-7 days",
  contributeHow5: "Get recognized as contributor when published",
  
  contributeIncentivesTitle: "Incentives for contributors",
  contributeIncentive1: "Formal citation in all Observatory publications",
  contributeIncentive2: "Digital collaboration certificate",
  contributeIncentive3: "Social media recognition",
  contributeIncentive4: "Valid for academic CV",
  contributeIncentive5: "Access to Observatory insights before public release",

  // Q17: Vision 2040 alignment
  q17Title: "How does this align with JCI Vision 2040?",
  q17Goal: "Vision 2040 Goal: Empower 500,000 young leaders by 2040",
  q17Contribution: "Observatory Contribution: Strategic intelligence to achieve this goal",
  
  vision2040PillarTitle: "Vision 2040 Pillar",
  vision2040RoleTitle: "Observatory Role",
  
  vision2040Pillar1: "Leadership Development Excellence",
  vision2040Role1: "Maps skills gaps (communication #1, critical thinking #2); identifies training priorities",
  
  vision2040Pillar2: "Technology & Digital Innovation",
  vision2040Role2: "Tracks digital adoption (jci.cc: 13,351 users, 260K social followers); guides tech roadmap",
  
  vision2040Pillar3: "Equity & Inclusion",
  vision2040Role3: "Reveals regional opportunity inequality (22-point YFI gap); identifies where impact needed most",
  
  vision2040Pillar4: "Sustainability & Climate",
  vision2040Role4: "Provides youth opportunity context for environmental policy alignment",
  
  vision2040QuestionsTitle: "Strategic Questions the Observatory Answers",
  vision2040Q1: "Where to expand? — Africa and the Middle East (lowest YFI = highest opportunity for impact)",
  vision2040Q2: "What to prioritize? — Mental health (40% YFI weight = biggest differentiator)",
  vision2040Q3: "How to scale? — Technology (80%+ adoption target by 2040)",
  vision2040Q4: "Success measures? — Improve global YFI 4-7 points; close Africa and Middle East gap by 50%",
  
  vision2040Summary: "The Observatory transforms Vision 2040 from an aspiration into an evidence-based strategy.",

  // Section 6: 2026 Plan of Action (EVOLVE)
  evolveTitle: "2026 Plan of Action: EVOLVE",

  // Q21: EVOLVE connection
  q21Title: "How does the Observatory relate to the 2026 Plan of Action?",
  q21Answer: "The 2026 Plan of Action establishes three core principles: LEAD | CONNECT | EVOLVE. The Observatory is a direct manifestation of the EVOLVE principle.",
  q21Quote: "'We need to LEAD with the focus our cause deserves, CONNECT with the change we want to create, and EVOLVE with the fast-moving world around us.'",
  
  evolveHow: "How the Observatory Enables EVOLVE:",
  
  evolve1Title: "Evolving Through Data-Driven Decision Making",
  evolve1: "The Observatory provides evidence showing which skills are most needed globally, where leadership development has greatest impact, and how to measure success through youth opportunity indicators.",
  evolve1Summary: "Without data, we cannot evolve strategically — we can only react. The Observatory enables proactive evolution based on evidence.",
  
  evolve2Title: "Supporting the 4 Action Pillars",
  actionPillar1: "Sustainability First",
  actionPillar1Desc: "Tracks mental health (40% YFI weight) and long-term youth opportunity, ensuring work creates sustainable futures",
  actionPillar2: "Building Global Leadership",
  actionPillar2Desc: "Maps where leadership development is most needed; validates that JCI programs develop skills young people prioritize",
  actionPillar3: "Business Across the World",
  actionPillar3Desc: "Provides entrepreneurship ecosystem data (30% YFI weight); identifies where JCI Business programs have greatest impact",
  actionPillar4: "Connecting Lives and Cultures",
  actionPillar4Desc: "Shows regional differences in youth opportunity (22-point YFI gap), demonstrating why cross-cultural connection matters",
  
  evolve3Title: "Measuring KPIs That Matter",
  evolve3: "The Observatory makes KPIs meaningful by connecting them to external youth opportunity data. For example, if we see 30% growth in Africa & Middle East membership, the Observatory shows this matters because that region has the lowest YFI (53) and greatest opportunity gap.",
  
  evolve4Title: "'Back to Basics' Meets Strategic Innovation",
  evolve4: "The Observatory complements the 2026 Plan's Phase 1 'Back to Basics' by providing strategic context for foundational programs, offering benchmarking data for trainer certification, and supporting Mentor-Mentee 2.0 with evidence.",
  
  evolve5Title: "Global Events With Purpose",
  evolve5: "The Observatory provides data that makes global events (Youth Leadership Summit, Sustainability Week, Female Leadership Forums) evidence-based rather than ceremonial.",
  
  evolveKeyConnection: "Key Connection: EVOLVE means adapting to the fast-moving world. The Observatory is how JCI evolves — from intuition-based to data-driven leadership, from 'we think we make a difference' to 'we can prove our impact.'",
  
  evolveSummary: "The 2026 Plan of Action sets the direction (LEAD | CONNECT | EVOLVE). The Observatory provides the intelligence that makes evolution strategic, measurable, and auditable.",

  // Section 7: Common Questions
  commonTitle: "Common Questions",

  // Q22: Privacy
  q22Title: "How does the Observatory address privacy and confidentiality?",
  
  privacyMemberTitle: "Member Privacy",
  privacyMember1: "No individual member data is published",
  privacyMember2: "All survey data is aggregated and anonymized",
  privacyMember3: "Respondents cannot be identified in results",
  privacyMember4: "Personal information is protected per privacy policy",
  
  privacyOrgTitle: "Organizational Data",
  privacyOrg: "Only aggregate data published (e.g., '147,670 members')",
  
  privacySecurityTitle: "Data Security",
  privacySecurity1: "Encrypted transmission and storage",
  privacySecurity2: "Regular security audits",
  privacySecurity3: "Limited access to raw data (only Operations Team)",
  privacySecurity4: "Compliance with GDPR and regional privacy laws",

  // Q23: Disagreement
  q23Title: "What if I disagree with the Observatory findings?",
  q23Answer: "Your feedback is essential. Disagreement helps us improve.",
  q23Process: "Here's how:",
  q23Step1: "Submit detailed feedback to tmariano@jci.cc",
  q23Step2: "Specify your concern (methodology, data accuracy, interpretation)",
  q23Step3: "Your feedback goes to the relevant Development Council for review",
  q23Step4: "Within 2 weeks, we'll explain our methodology or acknowledge your valid point",
  q23Step5: "If your feedback reveals an error, we correct it and cite you",
  q23Summary: "The Observatory evolves through constructive disagreement. We're not trying to be right — we're trying to be accurate.",

  // Q24: Staying updated
  q24Title: "How can I stay updated on Observatory developments?",
  
  newsletterTitle: "Newsletter (Launching Q2 2026)",
  newsletter1: "Monthly highlights of new findings",
  newsletter2: "Contributor spotlights",
  newsletter3: "Upcoming innovations",
  newsletter4: "Subscribe at the Auth page",
  
  webinarsTitle: "Webinars (Launching Q3 2026)",
  webinars1: "Expert explanations of Observatory dimensions",
  webinars2: "How to use Observatory data for strategic planning",
  webinars3: "Q&A with Development Councils",
  webinars4: "Free, open to all, multilingual",
  
  directEmailTitle: "Direct Email",
  directEmail: "tmariano@jci.cc — General inquiries, questions about specific data or methodology",

  // Contact Section
  contactTitle: "Contact & Resources",
  
  contactTechnicalTitle: "For Technical Questions",
  contactTechnical: "tmariano@jci.cc",
  
  contactRegionalTitle: "For Regional Questions",
  contactAmericas: "America: adc@jci.cc",
  contactEurope: "Europe: edc@jci.cc",
  contactAfrica: "Africa & Middle East: amdec@jci.cc",
  contactAsia: "Asia & Pacific: apdc@jci.cc",
  
  keyResourcesTitle: "Key Resources",
  keyResource1: "Full Methodology",
  keyResource2: "Data Dashboard",
  keyResource3: "How to Contribute",
  
  accessibilityTitle: "Accessibility",
  accessibility1: "Available in English, Portuguese, Spanish, French and Japanese",
  accessibility2: "Downloadable PDFs of all content (coming soon)",
  accessibility3: "Screen-reader compatible",
  accessibility4: "Additional languages on request",

  // CTA
  ctaTitle: "Ready to Explore?",
  ctaDescription: "The Youth Futures Observatory is built on evidence. Our commitment is to transparency, rigor, and continuous improvement in support of JCI's mission to develop leaders for a changing world.",
  ctaExplore: "Explore the Observatory",
  ctaContribute: "Contribute to the Observatory",
  ctaContact: "Contact Us",

  // Source
  sourceAttribution: "Source: JCI Strategic Plan 2023-2027, DASHCity Methodology, JCI Impact Report 2025",
};
